import React, { useEffect, useState } from 'react';
import { api } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { DollarSign, TrendingUp, Receipt, Cake, Package, UtensilsCrossed, Filter, Download } from 'lucide-react';
import { exportToExcel, exportBranchWiseBusinessSummary } from '../utils/excelExport';

export default function Reports() {
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({
    branch_id: '',
    start_date: '',
    end_date: ''
  });
  const [businessSummary, setBusinessSummary] = useState(null);
  const [foodReport, setFoodReport] = useState([]);
  const [cakeReport, setCakeReport] = useState([]);
  const [decorationReport, setDecorationReport] = useState([]);
  const [expenseReport, setExpenseReport] = useState(null);
  const [cashBalanceReport, setCashBalanceReport] = useState(null);

  useEffect(() => {
    fetchBranches();
  }, []);

  const fetchBranches = async () => {
    try {
      const response = await api.get('/branches');
      setBranches(response.data);
    } catch (error) {
      toast.error('Failed to fetch branches');
    }
  };

  const buildQueryParams = () => {
    const params = new URLSearchParams();
    if (filters.branch_id && filters.branch_id !== 'all') params.append('branch_id', filters.branch_id);
    if (filters.start_date) params.append('start_date', filters.start_date);
    if (filters.end_date) params.append('end_date', filters.end_date);
    return params.toString();
  };

  const fetchBusinessSummary = async () => {
    setLoading(true);
    try {
      const query = buildQueryParams();
      const response = await api.get(`/reports/business-summary${query ? '?' + query : ''}`);
      setBusinessSummary(response.data);
    } catch (error) {
      toast.error('Failed to fetch business summary');
    } finally {
      setLoading(false);
    }
  };

  const fetchFoodReport = async () => {
    setLoading(true);
    try {
      const query = buildQueryParams();
      const response = await api.get(`/reports/food-beverages${query ? '?' + query : ''}`);
      setFoodReport(response.data);
    } catch (error) {
      toast.error('Failed to fetch food report');
    } finally {
      setLoading(false);
    }
  };

  const fetchCakeReport = async () => {
    setLoading(true);
    try {
      const query = buildQueryParams();
      const response = await api.get(`/reports/cakes${query ? '?' + query : ''}`);
      setCakeReport(response.data);
    } catch (error) {
      toast.error('Failed to fetch cake report');
    } finally {
      setLoading(false);
    }
  };

  const fetchDecorationReport = async () => {
    setLoading(true);
    try {
      const query = buildQueryParams();
      const response = await api.get(`/reports/decorations${query ? '?' + query : ''}`);
      setDecorationReport(response.data);
    } catch (error) {
      toast.error('Failed to fetch decoration report');
    } finally {
      setLoading(false);
    }
  };

  const fetchExpenseReport = async () => {
    setLoading(true);
    try {
      const query = buildQueryParams();
      const response = await api.get(`/reports/expenses${query ? '?' + query : ''}&include_owner=true`);
      setExpenseReport(response.data);
    } catch (error) {
      toast.error('Failed to fetch expense report');
    } finally {
      setLoading(false);
    }
  };

  const fetchCashBalanceReport = async () => {
    setLoading(true);
    try {
      const query = buildQueryParams();
      const response = await api.get(`/reports/cash-balance${query ? '?' + query : ''}`);
      setCashBalanceReport(response.data);
    } catch (error) {
      toast.error('Failed to fetch cash balance report');
    } finally {
      setLoading(false);
    }
  };

  const exportBusinessSummary = () => {
    if (!businessSummary) return;
    const data = [{
      'Total Bills': businessSummary.total_bills,
      'Total Revenue': businessSummary.total_revenue,
      'Total Advance': businessSummary.total_advance,
      'Cash Collected': businessSummary.total_cash,
      'Online Payments': businessSummary.total_online,
      'Total Discount': businessSummary.total_discount
    }];
    exportToExcel(data, 'business_summary', 'Business Summary');
    toast.success('Report exported successfully');
  };

  const exportBranchWiseReport = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.start_date) params.append('start_date', filters.start_date);
      if (filters.end_date) params.append('end_date', filters.end_date);
      
      const query = params.toString();
      const response = await api.get(`/reports/business-summary-by-branch${query ? '?' + query : ''}`);
      
      if (response.data.length === 0) {
        toast.error('No data available for export');
        return;
      }
      
      const dateRange = filters.start_date && filters.end_date 
        ? `${filters.start_date}_to_${filters.end_date}` 
        : '';
      
      exportBranchWiseBusinessSummary(response.data, dateRange);
      toast.success('Branch-wise report exported successfully');
    } catch (error) {
      toast.error('Failed to export branch-wise report');
    } finally {
      setLoading(false);
    }
  };

  const exportFoodReport = () => {
    if (foodReport.length === 0) return;
    exportToExcel(foodReport, 'food_beverages_report', 'Food & Beverages');
    toast.success('Report exported successfully');
  };

  const exportCakeReport = () => {
    if (cakeReport.length === 0) return;
    exportToExcel(cakeReport, 'cakes_report', 'Cakes');
    toast.success('Report exported successfully');
  };

  const exportDecorationReport = () => {
    if (decorationReport.length === 0) return;
    exportToExcel(decorationReport, 'decorations_report', 'Decorations');
    toast.success('Report exported successfully');
  };

  const exportExpenseReport = () => {
    if (!expenseReport) return;
    const data = [{
      'Total Branch Expenses': expenseReport.total_branch_expenses,
      'Branch Expenses Count': expenseReport.branch_expenses_count,
      'Total Owner Expenses': expenseReport.total_owner_expenses,
      'Owner Expenses Count': expenseReport.owner_expenses_count,
      'Total Expenses': expenseReport.total_expenses
    }];
    exportToExcel(data, 'expense_report', 'Expense Summary');
    toast.success('Report exported successfully');
  };

  return (
    <div className="space-y-6 animate-fade-in">
      <div>
        <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Space Grotesk' }} data-testid="reports-title">
          Reports & Analytics
        </h1>
        <p className="text-slate-600 mt-1">View detailed business insights</p>
      </div>

      {/* Filters */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Filter size={20} />
            Report Filters
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label>Branch</Label>
              <Select value={filters.branch_id} onValueChange={(value) => setFilters({ ...filters, branch_id: value })}>
                <SelectTrigger data-testid="report-branch-filter">
                  <SelectValue placeholder="All branches" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All branches</SelectItem>
                  {branches.map(branch => (
                    <SelectItem key={branch.id} value={branch.id}>{branch.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Start Date</Label>
              <Input
                data-testid="report-start-date"
                type="date"
                value={filters.start_date}
                onChange={(e) => setFilters({ ...filters, start_date: e.target.value })}
              />
            </div>

            <div className="space-y-2">
              <Label>End Date</Label>
              <Input
                data-testid="report-end-date"
                type="date"
                value={filters.end_date}
                onChange={(e) => setFilters({ ...filters, end_date: e.target.value })}
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Reports Tabs */}
      <Tabs defaultValue="business" className="space-y-4">
        <TabsList className="grid w-full grid-cols-6">
          <TabsTrigger value="business" data-testid="tab-business">Business</TabsTrigger>
          <TabsTrigger value="food" data-testid="tab-food">Food & Beverages</TabsTrigger>
          <TabsTrigger value="cakes" data-testid="tab-cakes">Cakes</TabsTrigger>
          <TabsTrigger value="decorations" data-testid="tab-decorations">Decorations</TabsTrigger>
          <TabsTrigger value="expenses" data-testid="tab-expenses">Expenses</TabsTrigger>
          <TabsTrigger value="cash-balance" data-testid="tab-cash-balance">Cash Balance</TabsTrigger>
        </TabsList>

        <TabsContent value="business">
          <div className="space-y-4">
            <div className="flex gap-2">
              <Button data-testid="fetch-business-report" onClick={fetchBusinessSummary} disabled={loading}>
                {loading ? 'Loading...' : 'Generate Report'}
              </Button>
              {businessSummary && (
                <Button onClick={exportBusinessSummary} variant="outline" className="gap-2">
                  <Download size={16} /> Export Summary
                </Button>
              )}
              <Button onClick={exportBranchWiseReport} variant="outline" className="gap-2" disabled={loading}>
                <Download size={16} /> Export Branch-wise
              </Button>
            </div>

            {businessSummary && (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <Card>
                  <CardContent className="pt-6">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-blue-100 rounded-lg">
                        <Receipt className="w-5 h-5 text-blue-600" />
                      </div>
                      <div>
                        <p className="text-sm text-slate-600">Total Bills</p>
                        <p className="text-2xl font-bold" data-testid="total-bills-stat">{businessSummary.total_bills}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardContent className="pt-6">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-green-100 rounded-lg">
                        <DollarSign className="w-5 h-5 text-green-600" />
                      </div>
                      <div>
                        <p className="text-sm text-slate-600">Total Revenue</p>
                        <p className="text-2xl font-bold text-green-600" data-testid="total-revenue-stat">₹{businessSummary.total_revenue.toFixed(2)}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardContent className="pt-6">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-amber-100 rounded-lg">
                        <DollarSign className="w-5 h-5 text-amber-600" />
                      </div>
                      <div>
                        <p className="text-sm text-slate-600">Total Advance Received</p>
                        <p className="text-2xl font-bold text-amber-600" data-testid="total-advance-stat">₹{businessSummary.total_advance.toFixed(2)}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardContent className="pt-6">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-purple-100 rounded-lg">
                        <TrendingUp className="w-5 h-5 text-purple-600" />
                      </div>
                      <div>
                        <p className="text-sm text-slate-600">Cash Collected</p>
                        <p className="text-2xl font-bold text-purple-600" data-testid="cash-collected-stat">₹{businessSummary.total_cash.toFixed(2)}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardContent className="pt-6">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-indigo-100 rounded-lg">
                        <DollarSign className="w-5 h-5 text-indigo-600" />
                      </div>
                      <div>
                        <p className="text-sm text-slate-600">Online Payments</p>
                        <p className="text-2xl font-bold text-indigo-600" data-testid="online-amount-stat">₹{businessSummary.total_online.toFixed(2)}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}
          </div>
        </TabsContent>

        <TabsContent value="food">
          <div className="space-y-4">
            <div className="flex gap-2">
              <Button data-testid="fetch-food-report" onClick={fetchFoodReport} disabled={loading}>
                {loading ? 'Loading...' : 'Generate Report'}
              </Button>
              {foodReport.length > 0 && (
                <Button onClick={exportFoodReport} variant="outline" className="gap-2">
                  <Download size={16} /> Export
                </Button>
              )}
            </div>

            {foodReport.length > 0 && (
              <Card>
                <CardContent className="p-0">
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-slate-50 border-b">
                        <tr>
                          <th className="px-4 py-3 text-left text-sm font-semibold">Item Name</th>
                          <th className="px-4 py-3 text-right text-sm font-semibold">Quantity Sold</th>
                          <th className="px-4 py-3 text-right text-sm font-semibold">Total Amount</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y">
                        {foodReport.map((item, idx) => (
                          <tr key={idx} data-testid={`food-item-${idx}`}>
                            <td className="px-4 py-3 text-sm">{item.item_name}</td>
                            <td className="px-4 py-3 text-sm text-right">{item.quantity}</td>
                            <td className="px-4 py-3 text-sm text-right font-semibold">₹{item.total_amount.toFixed(2)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </TabsContent>

        <TabsContent value="cakes">
          <div className="space-y-4">
            <div className="flex gap-2">
              <Button data-testid="fetch-cake-report" onClick={fetchCakeReport} disabled={loading}>
                {loading ? 'Loading...' : 'Generate Report'}
              </Button>
              {cakeReport.length > 0 && (
                <Button onClick={exportCakeReport} variant="outline" className="gap-2">
                  <Download size={16} /> Export
                </Button>
              )}
            </div>

            {cakeReport.length > 0 && (
              <Card>
                <CardContent className="p-0">
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-slate-50 border-b">
                        <tr>
                          <th className="px-4 py-3 text-left text-sm font-semibold">Cake Name</th>
                          <th className="px-4 py-3 text-right text-sm font-semibold">Quantity Sold</th>
                          <th className="px-4 py-3 text-right text-sm font-semibold">Total Amount</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y">
                        {cakeReport.map((item, idx) => (
                          <tr key={idx} data-testid={`cake-item-${idx}`}>
                            <td className="px-4 py-3 text-sm">{item.item_name}</td>
                            <td className="px-4 py-3 text-sm text-right">{item.quantity}</td>
                            <td className="px-4 py-3 text-sm text-right font-semibold">₹{item.total_amount.toFixed(2)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </TabsContent>

        <TabsContent value="decorations">
          <div className="space-y-4">
            <div className="flex gap-2">
              <Button data-testid="fetch-decoration-report" onClick={fetchDecorationReport} disabled={loading}>
                {loading ? 'Loading...' : 'Generate Report'}
              </Button>
              {decorationReport.length > 0 && (
                <Button onClick={exportDecorationReport} variant="outline" className="gap-2">
                  <Download size={16} /> Export
                </Button>
              )}
            </div>

            {decorationReport.length > 0 && (
              <Card>
                <CardContent className="p-0">
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-slate-50 border-b">
                        <tr>
                          <th className="px-4 py-3 text-left text-sm font-semibold">Decoration Name</th>
                          <th className="px-4 py-3 text-right text-sm font-semibold">Quantity Sold</th>
                          <th className="px-4 py-3 text-right text-sm font-semibold">Total Amount</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y">
                        {decorationReport.map((item, idx) => (
                          <tr key={idx} data-testid={`decoration-item-${idx}`}>
                            <td className="px-4 py-3 text-sm">{item.item_name}</td>
                            <td className="px-4 py-3 text-sm text-right">{item.quantity}</td>
                            <td className="px-4 py-3 text-sm text-right font-semibold">₹{item.total_amount.toFixed(2)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </TabsContent>

        <TabsContent value="expenses">
          <div className="space-y-4">
            <div className="flex gap-2">
              <Button data-testid="fetch-expense-report" onClick={fetchExpenseReport} disabled={loading}>
                {loading ? 'Loading...' : 'Generate Report'}
              </Button>
              {expenseReport && (
                <Button onClick={exportExpenseReport} variant="outline" className="gap-2">
                  <Download size={16} /> Export
                </Button>
              )}
            </div>

            {expenseReport && (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Card>
                  <CardContent className="pt-6">
                    <p className="text-sm text-slate-600">Branch Expenses</p>
                    <p className="text-2xl font-bold text-orange-600" data-testid="branch-expenses-stat">₹{expenseReport.total_branch_expenses.toFixed(2)}</p>
                    <p className="text-xs text-slate-500 mt-1">{expenseReport.branch_expenses_count} entries</p>
                  </CardContent>
                </Card>

                <Card>
                  <CardContent className="pt-6">
                    <p className="text-sm text-slate-600">Owner Expenses</p>
                    <p className="text-2xl font-bold text-red-600" data-testid="owner-expenses-stat">₹{expenseReport.total_owner_expenses.toFixed(2)}</p>
                    <p className="text-xs text-slate-500 mt-1">{expenseReport.owner_expenses_count} entries</p>
                  </CardContent>
                </Card>

                <Card>
                  <CardContent className="pt-6">
                    <p className="text-sm text-slate-600">Total Expenses</p>
                    <p className="text-2xl font-bold text-slate-900" data-testid="total-expenses-stat">₹{expenseReport.total_expenses.toFixed(2)}</p>
                  </CardContent>
                </Card>
              </div>
            )}
          </div>
        </TabsContent>

        <TabsContent value="cash-balance">
          <div className="space-y-4">
            <div className="flex gap-2">
              <Button data-testid="fetch-cash-balance-report" onClick={fetchCashBalanceReport} disabled={loading}>
                {loading ? 'Loading...' : 'Generate Report'}
              </Button>
            </div>

            {cashBalanceReport && (
              <>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <Card>
                    <CardContent className="pt-6">
                      <p className="text-sm text-slate-600">Total Cash Collected</p>
                      <p className="text-2xl font-bold text-green-600" data-testid="cash-collected-stat">₹{cashBalanceReport.total_cash_collected.toFixed(2)}</p>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardContent className="pt-6">
                      <p className="text-sm text-slate-600">Total Cash Expenses</p>
                      <p className="text-2xl font-bold text-red-600" data-testid="cash-expenses-stat">₹{cashBalanceReport.total_cash_expenses.toFixed(2)}</p>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardContent className="pt-6">
                      <p className="text-sm text-slate-600">Net Cash Balance</p>
                      <p className="text-2xl font-bold text-blue-600" data-testid="cash-balance-stat">₹{cashBalanceReport.cash_balance.toFixed(2)}</p>
                    </CardContent>
                  </Card>
                </div>

                {/* Branch-wise Cash Balance */}
                {cashBalanceReport.branch_wise_balance && cashBalanceReport.branch_wise_balance.length > 0 && (
                  <Card>
                    <CardHeader>
                      <CardTitle>Branch-Wise Cash Balance</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="overflow-x-auto">
                        <table className="w-full">
                          <thead className="bg-slate-50">
                            <tr>
                              <th className="text-left p-3 font-semibold">Branch</th>
                              <th className="text-right p-3 font-semibold">Cash Collected</th>
                              <th className="text-right p-3 font-semibold">Cash Expenses</th>
                              <th className="text-right p-3 font-semibold">Cash Balance</th>
                            </tr>
                          </thead>
                          <tbody>
                            {cashBalanceReport.branch_wise_balance.map((branch) => (
                              <tr key={branch.branch_id} className="border-t">
                                <td className="p-3">{branch.branch_name}</td>
                                <td className="text-right p-3 text-green-600">₹{branch.cash_collected.toFixed(2)}</td>
                                <td className="text-right p-3 text-red-600">₹{branch.cash_expenses.toFixed(2)}</td>
                                <td className={`text-right p-3 font-bold ${branch.cash_balance >= 0 ? 'text-blue-600' : 'text-red-600'}`}>
                                  ₹{branch.cash_balance.toFixed(2)}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </CardContent>
                  </Card>
                )}
              </>
            )}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
